import { useTranslation } from 'react-i18next';
import { useGlobalStoreShallow } from 'src/store/globalStore';

import {
    Button,
    Modal,
    ModalComponent,
    ModalProps,
} from '@speeki/global-ui-components';
import { SessionStorageManager } from '@speeki/storage-manager';

import { TenantNameFragmentDoc } from '@graphql/generated/graphql';

import { APIService } from '@services/ApiService';
import { AuthService } from '@services/AuthService/AuthService';

import { useFragment, useReactiveVar } from '@apollo/client';
import {
    subsidiarySiteUuidFromUrlVar,
    switchCompanyMetadataVar,
} from '@boot/reactiveVariables';

export interface SwitchCompanyMetadata {
    apiUrl?: string;
    code?: string;
    currentTenantUuid?: string | null;
    name?: string;
    subsidiarySiteUuid?: string | null;
    uuid?: string;
}

interface SwitchCompanyModalProps
    extends Pick<ModalProps, 'onClose' | 'open'> {}

export const SwitchCompanyModal = ({
    onClose,
    open,
}: SwitchCompanyModalProps) => {
    const metadata = useReactiveVar(switchCompanyMetadataVar);

    const { t } = useTranslation();
    const { setCompanyInfo } = useGlobalStoreShallow(['setCompanyInfo']);

    const { apiUrl, code, currentTenantUuid, name, subsidiarySiteUuid, uuid } =
        metadata || {};

    const { data } = useFragment({
        fragment: TenantNameFragmentDoc,
        from: `TenantBaseDataType:${currentTenantUuid}`,
    });

    const handleCancel = () => {
        onClose();
    };

    const handleSubmit = async () => {
        if (!uuid || !apiUrl || !code || !name) return;
        await AuthService.logout(false, { keepTenantData: true });

        setCompanyInfo({
            uuid,
            apiUrl,
            code,
            name,
        });

        if (subsidiarySiteUuid) {
            subsidiarySiteUuidFromUrlVar(subsidiarySiteUuid);
            SessionStorageManager.setValue(
                'subsidiarySite',
                subsidiarySiteUuid,
            );
        }

        new APIService().setGraphAPI = apiUrl;
        AuthService.setTenantUuid(uuid);
        onClose();
    };

    return (
        <ModalComponent
            onClose={onClose}
            open={open}
            preventCloseEsc
            preventCloseOutside
            width={648}
        >
            <Modal.Content variant="secondary">
                <Modal.Header>
                    <Modal.TitleSecondary center>
                        Switching the company
                    </Modal.TitleSecondary>
                    <Modal.Description center>
                        {t('REPLACE_COMPANY_TEXT_1')} ({name}){' '}
                        {t('REPLACE_COMPANY_TEXT_2')} ({data.name}).{' '}
                        {t('REPLACE_COMPANY_TEXT_3')}
                    </Modal.Description>
                </Modal.Header>
            </Modal.Content>

            <Modal.Footer>
                <Modal.ButtonsWrapper>
                    <Button
                        onClick={handleCancel}
                        testId="5h8oQxP"
                        variant={'secondary'}
                    >
                        {t('CANCEL')}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        testId="15SG44w"
                        variant={'primary'}
                    >
                        {t('CONFIRM')}
                    </Button>
                </Modal.ButtonsWrapper>
            </Modal.Footer>
        </ModalComponent>
    );
};
